import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { GoogleContactRecipientCombobox } from '@/components/email/GoogleContactRecipientCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Mail,
  Send,
  Inbox,
  Trash2,
  RefreshCw,
  Plus,
  X,
  Reply,
  Clock,
  User,
  RotateCcw,
  Trash,
  Paperclip,
  Download,
  Search,
  Link2,
} from 'lucide-react';

interface EmailAttachment {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  downloadUrl: string;
}

interface Email {
  id: string;
  fromEmail: string;
  toEmail: string;
  subject: string;
  content: string;
  htmlContent?: string;
  folder: 'INBOX' | 'SENT' | 'TRASH';
  isRead: boolean;
  sentAt?: string;
  receivedAt?: string;
  createdAt: string;
  attachments?: EmailAttachment[];
}

type FolderType = 'INBOX' | 'SENT' | 'TRASH';

function stripPreview(text: string, max = 120): string {
  const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!plain) return '';
  return plain.length > max ? `${plain.slice(0, max)}…` : plain;
}

function formatListDate(dateString?: string): string {
  if (!dateString) return '';
  const d = new Date(dateString);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ieri';
  if (now.getFullYear() === d.getFullYear()) {
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  }
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatFullDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayAddress(raw: string): string {
  const trimmed = raw.trim();
  const nameMatch = trimmed.match(/^"?([^"<]+)"?\s*</);
  if (nameMatch?.[1]) return nameMatch[1].trim();
  const emailMatch = trimmed.match(/<([^>]+)>/);
  const addr = emailMatch?.[1] || trimmed;
  if (addr.includes('@')) {
    const local = addr.split('@')[0];
    return local.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return addr;
}

const EmailPage: React.FC = () => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentFolder, setCurrentFolder] = useState<FolderType>('INBOX');
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showNewEmailModal, setShowNewEmailModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [newEmail, setNewEmail] = useState({ to: '', subject: '', content: '' });
  const [newEmailFiles, setNewEmailFiles] = useState<File[]>([]);
  const [replyEmail, setReplyEmail] = useState({ to: '', subject: '', content: '' });
  const [replyEmailFiles, setReplyEmailFiles] = useState<File[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [gmailConnected, setGmailConnected] = useState<boolean | null>(null);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);

  const authHeaders = (): HeadersInit => ({
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  useEffect(() => {
    void loadMailbox();
  }, [currentFolder]);

  useEffect(() => {
    setSelectedIds([]);
    setSearchQuery('');
  }, [currentFolder]);

  const loadMailbox = async () => {
    setLoading(true);
    try {
      const statusRes = await fetch('/api/emails/connection-status', { headers: authHeaders() });
      const statusJson = await statusRes.json();
      const connected = Boolean(statusJson.success && statusJson.data?.connected);
      setGmailConnected(connected);
      setConnectedEmail(statusJson.data?.email ?? null);

      if (!connected) {
        setEmails([]);
        setSelectedEmail(null);
        setSelectedIds([]);
        setInboxUnreadCount(0);
        return;
      }

      const response = await fetch(`/api/emails?folder=${currentFolder}&limit=100`, {
        headers: authHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.data?.connected === false) {
          setGmailConnected(false);
          setEmails([]);
          return;
        }
        const list: Email[] = result.data?.emails || [];
        setEmails(list);
        if (currentFolder === 'INBOX') {
          setInboxUnreadCount(list.filter((e) => !e.isRead).length);
        }
      } else {
        setEmails([]);
      }
    } catch (error) {
      console.error('Error loading emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmails = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return emails;
    return emails.filter((e) => {
      const haystack = [e.fromEmail, e.toEmail, e.subject, e.content].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [emails, searchQuery]);

  const handleSync = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const response = await fetch('/api/emails/sync', {
        method: 'POST',
        headers: authHeaders(),
      });

      const result = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: result.message });
        void loadMailbox();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch {
      setMessage({ type: 'error', text: 'Errore nella sincronizzazione' });
    } finally {
      setSyncing(false);
    }
  };

  const sendEmailWithAttachments = async (
    payload: { to: string; subject: string; content: string },
    files: File[]
  ) => {
    const form = new FormData();
    form.append('to', payload.to);
    form.append('subject', payload.subject);
    form.append('content', payload.content);
    files.forEach((f) => form.append('attachments', f));

    const response = await fetch('/api/emails/send', {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    });
    const result = await response.json();
    return { ok: response.ok, ...result };
  };

  const handleSendEmail = async () => {
    try {
      const result = await sendEmailWithAttachments(newEmail, newEmailFiles);
      if (!result.ok || result.success === false) {
        setMessage({ type: 'error', text: result.message || 'Errore invio' });
        return;
      }
      setMessage({ type: 'success', text: 'Email inviata con successo!' });
      setShowNewEmailModal(false);
      setNewEmail({ to: '', subject: '', content: '' });
      setNewEmailFiles([]);
      if (currentFolder === 'SENT') void loadMailbox();
    } catch {
      setMessage({ type: 'error', text: "Errore nell'invio" });
    }
  };

  const handleDownloadAttachment = async (att: EmailAttachment) => {
    try {
      const response = await fetch(att.downloadUrl, { headers: authHeaders() });
      if (!response.ok) throw new Error('Download fallito');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.originalName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage({ type: 'error', text: 'Errore download allegato' });
    }
  };

  const handleSelectEmail = async (email: Email) => {
    try {
      const response = await fetch(`/api/emails/${email.id}`, { headers: authHeaders() });
      if (response.ok) {
        const result = await response.json();
        setSelectedEmail(result.data);
      } else {
        setSelectedEmail(email);
      }
    } catch {
      setSelectedEmail(email);
    }

    if (!email.isRead && email.folder === 'INBOX') {
      try {
        await fetch(`/api/emails/${email.id}/read`, {
          method: 'PATCH',
          headers: authHeaders(),
        });
        setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, isRead: true } : e)));
        setInboxUnreadCount((c) => Math.max(0, c - 1));
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleDeleteEmail = async (emailId: string) => {
    if (!confirm('Spostare questa email nel cestino?')) return;
    try {
      const response = await fetch(`/api/emails/${emailId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Email spostata nel cestino' });
        setSelectedEmail(null);
        void loadMailbox();
      }
    } catch {
      setMessage({ type: 'error', text: "Errore nell'eliminazione" });
    }
  };

  const handleRestoreEmail = async (emailId: string) => {
    try {
      const response = await fetch(`/api/emails/${emailId}/restore`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Email ripristinata' });
        setSelectedEmail(null);
        void loadMailbox();
      }
    } catch {
      setMessage({ type: 'error', text: 'Errore nel ripristino' });
    }
  };

  const handlePermanentDelete = async (emailId: string) => {
    if (!confirm('Eliminare definitivamente questa email? Operazione irreversibile!')) return;
    try {
      const response = await fetch(`/api/emails/${emailId}/permanent`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Email eliminata definitivamente' });
        setSelectedEmail(null);
        void loadMailbox();
      }
    } catch {
      setMessage({ type: 'error', text: "Errore nell'eliminazione" });
    }
  };

  const toggleSelectEmail = (emailId: string) => {
    setSelectedIds((prev) =>
      prev.includes(emailId) ? prev.filter((id) => id !== emailId) : [...prev, emailId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredEmails.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredEmails.map((e) => e.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Spostare ${selectedIds.length} email nel cestino?`)) return;
    try {
      const response = await fetch('/api/emails/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: result.message });
        setSelectedIds([]);
        void loadMailbox();
      }
    } catch {
      setMessage({ type: 'error', text: "Errore nell'eliminazione multipla" });
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Eliminare definitivamente ${selectedIds.length} email? Operazione irreversibile!`)) return;
    try {
      const response = await fetch('/api/emails/bulk-permanent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: result.message });
        setSelectedIds([]);
        void loadMailbox();
      }
    } catch {
      setMessage({ type: 'error', text: "Errore nell'eliminazione definitiva multipla" });
    }
  };

  const handleReply = () => {
    if (!selectedEmail) return;
    setReplyEmail({
      to: selectedEmail.fromEmail,
      subject: selectedEmail.subject.startsWith('Re:')
        ? selectedEmail.subject
        : `Re: ${selectedEmail.subject}`,
      content: `\n\n--- Messaggio originale ---\nDa: ${selectedEmail.fromEmail}\nData: ${formatFullDate(selectedEmail.receivedAt || selectedEmail.sentAt || selectedEmail.createdAt)}\nOggetto: ${selectedEmail.subject}\n\n${selectedEmail.content}`,
    });
    setShowReplyModal(true);
  };

  const handleSendReply = async () => {
    if (!replyEmail.to || !replyEmail.subject || !replyEmail.content) {
      setMessage({ type: 'error', text: 'Tutti i campi sono obbligatori' });
      return;
    }
    try {
      const result = await sendEmailWithAttachments(replyEmail, replyEmailFiles);
      if (result.ok && result.success !== false) {
        setMessage({ type: 'success', text: 'Risposta inviata con successo' });
        setShowReplyModal(false);
        setReplyEmail({ to: '', subject: '', content: '' });
        setReplyEmailFiles([]);
        void loadMailbox();
      } else {
        setMessage({ type: 'error', text: result.message || "Errore nell'invio della risposta" });
      }
    } catch {
      setMessage({ type: 'error', text: "Errore nell'invio della risposta" });
    }
  };

  const getFolderIcon = (folder: FolderType) => {
    switch (folder) {
      case 'INBOX':
        return <Inbox className="h-4 w-4 shrink-0" />;
      case 'SENT':
        return <Send className="h-4 w-4 shrink-0" />;
      case 'TRASH':
        return <Trash2 className="h-4 w-4 shrink-0" />;
    }
  };

  const getFolderName = (folder: FolderType) => {
    switch (folder) {
      case 'INBOX':
        return 'Posta in arrivo';
      case 'SENT':
        return 'Posta inviata';
      case 'TRASH':
        return 'Cestino';
    }
  };

  const listCounterparty = (email: Email) =>
    email.folder === 'SENT' ? email.toEmail : email.fromEmail;

  const isUnreadInbox = (email: Email) => !email.isRead && email.folder === 'INBOX';

  return (
    <div className="flex min-h-[calc(100vh-7.5rem)] w-full min-w-0 flex-col">
      {/* Toolbar */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Email</h1>
          <p className="text-sm text-muted-foreground">
            {gmailConnected && connectedEmail
              ? `Collegato: ${connectedEmail}`
              : 'Casella Gmail condivisa dello studio'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleSync}
            disabled={syncing || gmailConnected !== true}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', syncing && 'animate-spin')} />
            {syncing ? 'Sincronizzazione…' : 'Sincronizza'}
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/contacts">
              <Link2 className="mr-2 h-4 w-4" />
              Rubrica
            </Link>
          </Button>
          <Button onClick={() => setShowNewEmailModal(true)} size="sm" disabled={gmailConnected !== true}>
            <Plus className="mr-2 h-4 w-4" />
            Nuova email
          </Button>
        </div>
      </div>

      {message && (
        <div
          className={cn(
            'mb-4 flex items-center justify-between rounded-lg border px-4 py-3 text-sm',
            message.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-900'
              : 'border-red-200 bg-red-50 text-red-900'
          )}
        >
          <span>{message.text}</span>
          <button type="button" onClick={() => setMessage(null)} className="opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {gmailConnected === false && !loading ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-xl border bg-card px-6 py-16 text-center shadow-sm">
          <Mail className="mb-4 h-16 w-16 text-muted-foreground/25" />
          <h2 className="text-lg font-semibold text-foreground">Gmail non collegato</h2>
          <p className="mt-2 max-w-md text-sm text-muted-foreground leading-relaxed">
            Nessuna email viene mostrata finché la casella non è collegata. Dopo la disconnessione la
            cache locale viene svuotata automaticamente.
          </p>
          <Button asChild className="mt-6">
            <Link to="/settings?section=email">
              <Link2 className="mr-2 h-4 w-4" />
              Collega da Impostazioni
            </Link>
          </Button>
        </div>
      ) : (
      /* Client layout */
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card shadow-sm lg:flex-row">
        {/* Folders */}
        <nav className="flex shrink-0 border-b bg-muted/40 lg:w-52 lg:flex-col lg:border-b-0 lg:border-r">
          <div className="flex w-full lg:flex-col lg:p-2">
            {(['INBOX', 'SENT', 'TRASH'] as FolderType[]).map((folder) => (
              <button
                key={folder}
                type="button"
                onClick={() => {
                  setCurrentFolder(folder);
                  setSelectedEmail(null);
                }}
                className={cn(
                  'flex flex-1 items-center gap-2.5 px-4 py-3 text-sm transition-colors lg:flex-none lg:rounded-lg lg:px-3',
                  currentFolder === folder
                    ? 'bg-background font-medium text-primary shadow-sm lg:border lg:border-border'
                    : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
                )}
              >
                {getFolderIcon(folder)}
                <span className="truncate">{getFolderName(folder)}</span>
                {folder === 'INBOX' && inboxUnreadCount > 0 && (
                  <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                    {inboxUnreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </nav>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {/* List */}
          <div className="flex w-full min-h-0 flex-col border-b bg-background lg:w-[min(38vw,480px)] lg:min-w-[320px] lg:shrink-0 lg:border-b-0 lg:border-r">
            <div className="space-y-3 border-b bg-muted/20 px-4 py-3">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-base font-semibold">{getFolderName(currentFolder)}</h2>
                <span className="text-xs text-muted-foreground">
                  {filteredEmails.length}
                  {searchQuery ? ` / ${emails.length}` : ''} email
                </span>
              </div>

              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cerca mittente, oggetto…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 bg-background pl-9 text-sm"
                />
              </div>

              {emails.length > 0 && (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <label className="flex cursor-pointer items-center gap-2 text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={
                        filteredEmails.length > 0 && selectedIds.length === filteredEmails.length
                      }
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-input text-primary"
                    />
                    {selectedIds.length > 0
                      ? `${selectedIds.length} selezionate`
                      : 'Seleziona tutto'}
                  </label>
                  {selectedIds.length > 0 && (
                    <div className="flex gap-1">
                      {currentFolder !== 'TRASH' ? (
                        <Button onClick={handleBulkDelete} size="sm" variant="outline" className="h-8">
                          <Trash2 className="mr-1 h-3.5 w-3.5" />
                          Cestina
                        </Button>
                      ) : (
                        <Button
                          onClick={handleBulkPermanentDelete}
                          size="sm"
                          variant="destructive"
                          className="h-8"
                        >
                          <Trash className="mr-1 h-3.5 w-3.5" />
                          Elimina
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                  <p className="text-sm">Caricamento…</p>
                </div>
              ) : filteredEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
                  <Mail className="h-10 w-10 opacity-30" />
                  <p className="text-sm">{searchQuery ? 'Nessun risultato' : 'Nessuna email'}</p>
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {filteredEmails.map((email) => {
                    const isSelected = selectedEmail?.id === email.id;
                    const unread = isUnreadInbox(email);
                    const preview = stripPreview(email.content);

                    return (
                      <li
                        key={email.id}
                        className={cn(
                          'group flex gap-2 px-3 py-3 transition-colors',
                          isSelected && 'bg-primary/5',
                          !isSelected && 'hover:bg-muted/50',
                          unread && !isSelected && 'bg-primary/[0.03]'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(email.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelectEmail(email.id);
                          }}
                          className="mt-1.5 h-4 w-4 shrink-0 rounded border-input"
                          aria-label="Seleziona email"
                        />
                        <button
                          type="button"
                          onClick={() => handleSelectEmail(email)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="flex items-start gap-2">
                            {unread && (
                              <span
                                className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary"
                                aria-hidden
                              />
                            )}
                            {!unread && <span className="mt-2 w-2 shrink-0" aria-hidden />}
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex items-baseline justify-between gap-2">
                                <span
                                  className={cn(
                                    'truncate text-[15px] leading-tight',
                                    unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/90'
                                  )}
                                >
                                  {displayAddress(listCounterparty(email))}
                                </span>
                                <time
                                  className="shrink-0 text-xs tabular-nums text-muted-foreground"
                                  dateTime={email.sentAt || email.receivedAt || email.createdAt}
                                >
                                  {formatListDate(email.sentAt || email.receivedAt || email.createdAt)}
                                </time>
                              </div>
                              <p
                                className={cn(
                                  'mb-1 line-clamp-1 text-sm leading-snug',
                                  unread ? 'font-medium text-foreground' : 'text-foreground/80'
                                )}
                              >
                                {email.subject || '(Senza oggetto)'}
                              </p>
                              {preview && (
                                <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                                  {preview}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Reading pane */}
          <div className="min-h-[280px] min-w-0 flex-1 overflow-y-auto bg-muted/10 lg:min-h-0">
            {selectedEmail ? (
              <article className="w-full min-w-0 p-5 sm:p-8">
                <header className="mb-6 border-b border-border pb-6">
                  <h2 className="mb-4 text-xl font-semibold leading-snug text-foreground sm:text-2xl">
                    {selectedEmail.subject || '(Senza oggetto)'}
                  </h2>

                  <dl className="space-y-2 text-sm">
                    <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                      <dt className="flex items-center gap-1.5 font-medium text-muted-foreground">
                        <User className="h-4 w-4" />
                        Da
                      </dt>
                      <dd className="text-foreground">{selectedEmail.fromEmail}</dd>
                    </div>
                    <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                      <dt className="flex items-center gap-1.5 font-medium text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        A
                      </dt>
                      <dd className="text-foreground">{selectedEmail.toEmail}</dd>
                    </div>
                    <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                      <dt className="flex items-center gap-1.5 font-medium text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        Data
                      </dt>
                      <dd className="text-foreground">
                        {formatFullDate(
                          selectedEmail.sentAt || selectedEmail.receivedAt || selectedEmail.createdAt
                        )}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {currentFolder !== 'TRASH' && (
                      <>
                        <Button variant="default" size="sm" onClick={handleReply}>
                          <Reply className="mr-2 h-4 w-4" />
                          Rispondi
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteEmail(selectedEmail.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Cestina
                        </Button>
                      </>
                    )}
                    {currentFolder === 'TRASH' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreEmail(selectedEmail.id)}
                        >
                          <RotateCcw className="mr-2 h-4 w-4" />
                          Ripristina
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handlePermanentDelete(selectedEmail.id)}
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Elimina definitivamente
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)} className="ml-auto">
                      <X className="mr-1 h-4 w-4" />
                      Chiudi
                    </Button>
                  </div>
                </header>

                <div className="rounded-lg border bg-background p-5 shadow-sm">
                  {selectedEmail.htmlContent ? (
                    <div
                      className="prose prose-sm max-w-none break-words text-foreground prose-a:text-primary"
                      dangerouslySetInnerHTML={{ __html: selectedEmail.htmlContent }}
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-foreground/90">
                      {selectedEmail.content}
                    </pre>
                  )}
                </div>

                {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                  <section className="mt-6">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Paperclip className="h-4 w-4" />
                      Allegati ({selectedEmail.attachments.length})
                    </h3>
                    <ul className="space-y-2">
                      {selectedEmail.attachments.map((att) => (
                        <li
                          key={att.id}
                          className="flex items-center justify-between gap-3 rounded-lg border bg-background px-4 py-3"
                        >
                          <span className="min-w-0 truncate text-sm">
                            {att.originalName}
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({Math.round(att.size / 1024)} KB)
                            </span>
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            onClick={() => handleDownloadAttachment(att)}
                          >
                            <Download className="mr-1 h-4 w-4" />
                            Scarica
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </article>
            ) : (
              <div className="flex h-full min-h-[240px] items-center justify-center p-8 text-center text-muted-foreground">
                <div>
                  <Mail className="mx-auto mb-4 h-14 w-14 opacity-20" />
                  <p className="text-base font-medium">Seleziona un&apos;email</p>
                  <p className="mt-1 text-sm">Il contenuto apparirà qui</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* New Email Modal */}
      {showNewEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-semibold">Nuova email</h3>
              <button type="button" onClick={() => setShowNewEmailModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 overflow-y-auto px-6 py-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">Destinatario *</label>
                <GoogleContactRecipientCombobox
                  value={newEmail.to}
                  onValueChange={(to) => setNewEmail({ ...newEmail, to })}
                  placeholder="Cerca in rubrica Google…"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Oggetto *</label>
                <Input
                  placeholder="Oggetto dell'email"
                  value={newEmail.subject}
                  onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Messaggio *</label>
                <textarea
                  className="min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Scrivi il tuo messaggio…"
                  value={newEmail.content}
                  onChange={(e) => setNewEmail({ ...newEmail, content: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Allegati</label>
                <Input
                  type="file"
                  multiple
                  onChange={(e) => setNewEmailFiles(Array.from(e.target.files || []))}
                />
                {newEmailFiles.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {newEmailFiles.length} file selezionati
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <Button variant="outline" onClick={() => setShowNewEmailModal(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={!newEmail.to || !newEmail.subject || !newEmail.content}
              >
                <Send className="mr-2 h-4 w-4" />
                Invia
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-semibold">Rispondi</h3>
              <button type="button" onClick={() => setShowReplyModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 overflow-y-auto px-6 py-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">Destinatario *</label>
                <GoogleContactRecipientCombobox
                  value={replyEmail.to}
                  onValueChange={(to) => setReplyEmail({ ...replyEmail, to })}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Oggetto *</label>
                <Input value={replyEmail.subject} onChange={(e) => setReplyEmail({ ...replyEmail, subject: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Messaggio *</label>
                <textarea
                  className="min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={replyEmail.content}
                  onChange={(e) => setReplyEmail({ ...replyEmail, content: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Allegati</label>
                <Input type="file" multiple onChange={(e) => setReplyEmailFiles(Array.from(e.target.files || []))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <Button variant="outline" onClick={() => setShowReplyModal(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleSendReply}
                disabled={!replyEmail.to || !replyEmail.subject || !replyEmail.content}
              >
                <Send className="mr-2 h-4 w-4" />
                Invia risposta
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailPage;
